import { ProductInventoryHistoryDoc } from '@gooduncles/gu-app-schema';
import { groupBy } from 'lodash-es';
import { useEffect, useState } from 'react';
import { WHERE } from 'src/utils/firebase/firebase-manager';
import { getCollectionCount, observeProductInventoryHistory } from 'src/utils/firebase/firebase-short-cut';

const MAX_HISTORIES = 500; // 최대 허용 히스토리 수

/**
 * 같은 초에 여러 건이 기록되면 timestamp(초 단위)로도 _timeCreate(ms 동률 빈번)로도
 * 발생 순서를 특정할 수 없다. 한 재고의 히스토리는 beforeQuantity -> afterQuantity 로
 * 이어지는 사슬이므로 그 연결을 따라가 실제 순서를 복원하고, 최신 건이 위로 오도록 뒤집는다.
 * 취소/되돌림으로 사슬이 끊기거나 순환하면 원래 순서를 유지한다.
 */
const orderByChain = (rows: ProductInventoryHistoryDoc[]) => {
  if (rows.length < 2) return rows;
  const byBefore = new Map(rows.map((row) => [row.beforeQuantity, row]));
  const afters = new Set(rows.map((row) => row.afterQuantity));
  const ordered: ProductInventoryHistoryDoc[] = [];
  let cursor = rows.find((row) => !afters.has(row.beforeQuantity));
  while (cursor && ordered.length < rows.length) {
    ordered.push(cursor);
    cursor = byBefore.get(cursor.afterQuantity);
  }
  return ordered.length === rows.length ? ordered.reverse() : rows;
};

/** 시간 내림차순 정렬 + 같은 초 안에서도 사슬 순서로 최신 건부터 */
const sortHistories = (histories: ProductInventoryHistoryDoc[]) =>
  Object.entries(groupBy(histories, 'timestamp'))
    .sort(([a], [b]) => b.localeCompare(a))
    .flatMap(([, rows]) => orderByChain(rows));

const useProductInventoryHistories = (inventoryId: string | null, startValue: string, endValue: string) => {
  const [loading, setLoading] = useState(false);
  const [productInventoryHistories, setProductInventoryHistories] = useState<ProductInventoryHistoryDoc[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    // 검색 조건 설정
    const wheres: WHERE[] = [];
    if (inventoryId) {
      wheres.push(['inventoryId', '==', inventoryId]);
    } else {
      setError('입력된 재고 id가 없습니다.');
      setLoading(false);
      return;
    }

    const checkCount = async () => {
      try {
        const countSnapshot = await getCollectionCount('productInventoryHistory', wheres, {
          sortKey: 'timestamp',
          orderBy: 'desc',
          startValue,
          endValue,
        });

        const count = countSnapshot.data().count;
        if (count > MAX_HISTORIES) {
          setError(
            `데이터가 너무 많습니다 (${count}건 / 최대 ${MAX_HISTORIES}건). 날짜 범위를 좁혀서 다시 시도해주세요.`
          );
          setLoading(false);
          return false;
        }
        return true;
      } catch (err) {
        setError('데이터 수를 확인하는 중 오류가 발생했습니다.');
        setLoading(false);
        return false;
      }
    };

    const initializeSubscription = async () => {
      const isValidCount = await checkCount();
      if (!isValidCount) return;

      const productInventoryHistory$ = observeProductInventoryHistory(wheres, {
        sortKey: 'timestamp',
        orderBy: 'desc',
        startValue,
        endValue,
      });

      const subscription = productInventoryHistory$.subscribe((histories) => {
        setProductInventoryHistories(sortHistories(histories));
        setLoading(false);
      });

      return subscription;
    };

    let subscription: any;
    initializeSubscription().then((sub) => {
      subscription = sub;
    });

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [inventoryId, startValue, endValue]);

  return { productInventoryHistories, loading, error };
};

export default useProductInventoryHistories;
